import { Injectable, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { Promise } from 'q';

export interface SynchronizationServiceWhenFinalizer {
  commit: (...actions: (() => void)[]) => void;
}

export interface SynchronizationServiceWithFinalizer {
  repeat: (...actions: (() => Observable<boolean>)[]) => void;
  commit: (...actions: ((doneNotifier: () => void) => void)[]) => void;
}


@Injectable({
  providedIn: 'root'
})
export class SynchronizationService {

  private actions: { [key: string]: (() => void)[] } = {};
  private states: { [key: string]: number } = {};

  private allocated = 0;

  constructor() { }

  public notify<T>(entity: Type<T>, ready: boolean) {    

    if (!entity['sync-name']) entity['sync-name'] = 'T' + (++this.allocated);

    if (typeof this.states[entity['sync-name']] == 'undefined') this.states[entity['sync-name']] = 0;
    this.states[entity['sync-name']] += ready ? -1 : 1;

    if (ready) {     
      for (let condition in this.actions) {
        if (this._ready(condition.split('|'))) while (this.actions[condition].length > 0) this.actions[condition].shift()();
      }      
    }
  }

  public with(...entities: Type<any>[]): SynchronizationServiceWithFinalizer {
    return {
      repeat: (...actions: (() => Observable<boolean>)[]) => {

        entities.commit(i => {

          if (!i['sync-name']) i['sync-name'] = 'T' + (++this.allocated);

          if (typeof this.states[i['sync-name']] == 'undefined') this.states[i['sync-name']] = 0;
          this.states[i['sync-name']] += actions.length;
        });

        actions.forEach(i => {

          let fn = ready => {
            if (ready) {
              entities.commit(i => this.notify(i, true));
            }
            else {
              i().subscribe(fn);
            }
          }

          i().subscribe(fn);
        });
      },

      commit: (...actions: ((doneNotifier: () => void) => void)[]) => {

        entities.commit(i => {

          if (!i['sync-name']) i['sync-name'] = 'T' + (++this.allocated);

          if (typeof this.states[i['sync-name']] == 'undefined') this.states[i['sync-name']] = 0;
          this.states[i['sync-name']] += actions.length;
        });

        let doneNotifier = () => entities.commit(i => this.notify(i, true));

        actions.forEach(i => i(doneNotifier));
      }
    }
  }

  private _ready(conditions: string[]) {
    for (let name of conditions) if (typeof this.states[name] != 'undefined' && this.states[name] > 0) return false;
    return true;
  }

  public ready(...conditions: Type<any>[]): boolean {

    conditions.forEach(i => {
      if (!i['sync-name']) i['sync-name'] = 'T' + (++this.allocated);
    });

    return this._ready(conditions.map(i => i['sync-name']));
  }

  public when(...conditions: Type<any>[]): SynchronizationServiceWhenFinalizer {

    conditions.forEach(i => {
      if (!i['sync-name']) i['sync-name'] = 'T' + (++this.allocated);
    });

    return {

      commit: (action: () => void) => {

        if (this._ready(conditions.map(i => i['sync-name']))) {
          action();
        }
        else {

          let condition = conditions.map(i => i['sync-name']).distinct().join('|');
          if (!this.actions[condition]) this.actions[condition] = [];
          this.actions[condition].push(action);
        }
      }
    };    
  }
}
